import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import type { IAttachment } from '@pulse/shared';
import { ATTACHMENT_POLICY } from '@pulse/shared';
import { Box, Button, FormHelperText, IconButton, List, ListItem, ListItemText, Stack, Tooltip, Typography } from '@pulse/ui';
import type { ChangeEvent } from 'react';
import { useState } from 'react';

import { formatSize, validateAttachmentSize } from '../lib/attachment-max-size.util';

import { RemoveConfirmDialog } from './RemoveConfirmDialog';

// Generic multi-file attachment UI (§1) reused by Risks, Dependencies, Escalations, and Change
// Requests — presentational only; the caller wires in its own fetch/upload/remove/download
// hooks so this component stays API-agnostic. Enforces the same policy client-side that
// AttachmentService enforces authoritatively server-side. Delete is gated behind a confirmation
// dialog here so every caller gets it without duplicating the wiring.
interface AttachmentsPanelProps {
  attachments: IAttachment[];
  isLoading?: boolean;
  onUpload: (file: File) => void;
  isUploading?: boolean;
  onRemove: (attachmentId: string) => void;
  isRemoving?: boolean;
  downloadUrl: (attachmentId: string) => string;
  /** Hides upload/remove controls for callers without write permission on the owning entity. */
  disabled?: boolean;
}

export function AttachmentsPanel({
  attachments,
  isLoading,
  onUpload,
  isUploading,
  onRemove,
  isRemoving,
  downloadUrl,
  disabled,
}: AttachmentsPanelProps) {
  const [pendingRemove, setPendingRemove] = useState<IAttachment | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const atLimit = attachments.length >= ATTACHMENT_POLICY.maxFiles;

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const error = validateAttachmentSize(file);
    setSizeError(error);
    if (!error) onUpload(file);
  }

  return (
    <Box>
      {isLoading ? (
        <Typography variant="body2" color="text.secondary">Loading attachments…</Typography>
      ) : attachments.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No attachments.</Typography>
      ) : (
        <List dense disablePadding>
          {attachments.map((a) => (
            <ListItem
              key={a.id}
              disableGutters
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Download">
                    <IconButton size="small" component="a" href={downloadUrl(a.id)} target="_blank" rel="noreferrer">
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {!disabled && (
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => setPendingRemove(a)} aria-label={`Remove ${a.filename}`}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              }
            >
              <ListItemText
                primary={a.filename}
                secondary={`${formatSize(a.size)} · uploaded by ${a.uploadedBy.name}`}
              />
            </ListItem>
          ))}
        </List>
      )}

      {!disabled && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            component="label"
            color={sizeError ? 'error' : undefined}
            startIcon={<AttachFileIcon />}
            disabled={atLimit || isUploading}
          >
            {isUploading ? 'Uploading…' : atLimit ? `Maximum ${ATTACHMENT_POLICY.maxFiles} files reached` : 'Attach a file'}
            <input type="file" hidden onChange={handleFileChange} />
          </Button>
          {sizeError && (
            <FormHelperText error sx={{ mx: '14px' }}>
              {sizeError}
            </FormHelperText>
          )}
          {/* Uploads/removes here call their own API right away, unlike the rest of this form —
              worded to hold true whether this panel sits in a Cancel/Save dialog or a plain page. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mx: '14px', mt: 0.5 }}>
            Attaching or removing a file here happens right away — it doesn&apos;t wait for the rest of this form to be saved.
          </Typography>
        </Box>
      )}

      <RemoveConfirmDialog
        open={pendingRemove !== null}
        title={`Remove "${pendingRemove?.filename ?? ''}"?`}
        description="This will permanently remove the attachment."
        isPending={Boolean(isRemoving)}
        onCancel={() => setPendingRemove(null)}
        onConfirm={() => {
          const target = pendingRemove;
          setPendingRemove(null);
          if (target) onRemove(target.id);
        }}
      />
    </Box>
  );
}
